import { Component, ViewContainerRef, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as moment from 'moment';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { SeoService } from '@wdpr/ra-angular-seo-metadata';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { ConfigService } from '@finder/core/config.service';
import { CONFIG_CONSTANTS, LOCALE_DEFAULT } from '@finder/shared/constants/app.constants';
import { FinderThemeService } from '@finder/shared/services/theme-service/finder-theme.service';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
// import { NativeBridgeService } from '@finder/shared/services/native-bridge/native-bridge.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { FinderPageKeyService } from '@finder/shared/services/finder-page-key/finder-page-key.service';
import { LoadingServiceErrorState } from '@finder/shared/services/loading-service/loading-service.interface';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { loadCssResource } from '@finder/shared/utils/utils.dom';
import { LayoutService } from '@finder/shared/services/layout/layout.service';
import { CustomLayoutProperties } from '@finder/shared/services/layout/layout.interface';
import { DineToFinderErrorService } from '@finder/shared/services/dine-to-finder-error/dine-to-finder-error.service';
import { RouteStateService } from '@finder/shared/services/route-state-service/route-state-service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    syndicatedLayoutConfigs: Object;
    siteId: string;
    environment: string;
    apiBase: string;
    theme;
    dataLoaded = false;
    dataError: LoadingServiceErrorState;
    languageBundle: any;
    isNativeEmbedded = false;
    useSyndicated = true;
    currentCustomHeader;
    currentCustomFooter;
    private destroy$ = new Subject<void>();

    @ViewChild('rootComponents', { read: ViewContainerRef, static: true }) rootComponents: ViewContainerRef;
    @ViewChild('customHeaderTemplate', { read: ViewContainerRef, static: true }) customHeader: ViewContainerRef;
    @ViewChild('customFooterTemplate', { read: ViewContainerRef, static: true }) customFooter: ViewContainerRef;

    constructor(
        private configService: ConfigService,
        private themeSvc: FinderThemeService,
        private loadingSvc: LoadingService,
        private localeService: LocaleService,
        private localeHelperService: LocaleHelperService,
        public viewContainerRef: ViewContainerRef,
        private winRef: WindowRef,
        private elementRef: ElementRef,
        private seoService: SeoService,
        private finderSvc: FinderApiConnectorService,
        private finderAnalyticsService: FinderAnalyticsService,
        // private nativeBridge: NativeBridgeService,
        private lazyLoader: LazyLoaderService,
        private pageKeyService: FinderPageKeyService, // Do not remove - need this to listen
        private urlParamsService: UrlParamsService,
        private layoutService: LayoutService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private routeState: RouteStateService,
        private dineToFinderError: DineToFinderErrorService,
    ) {
        // this.isNativeEmbedded = this.nativeBridge.isEmbedded();
        this.syndicatedLayoutConfigs = this.configService.getValue('syndicatedLayout');
        this.siteId = this.configService.getValue('siteId');
        this.environment = this.configService.getValue('environment');
        this.apiBase =
            `${this.configService.getValue(CONFIG_CONSTANTS.apiUrlKey)}/${this.configService.getValue(CONFIG_CONSTANTS.defaultVersionKey)}`;

        this.setupLocale();
        this.setupRouteParams();
        this.setupTheme();
        this.setupLoadingService();
        this.setupLanguageBundle();
        this.setupSeo();
        this.setupAnalytics();
        this.setupCssOverride();
        this.setupLayout();
    }

    ngOnInit() {
        this.lazyLoader.lazyLoadCssResource('lazyload-style.css');
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    setupLocale() {
        this.routeState.pathParam()
            .pipe(
                map((params: any) => params.locale),
                // avoid to set the same locale multiple times
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((localeParam) => {
                /**
                 * When WorldServer is ready we can remove this method and the Interceptor
                 */
                let locale = localeParam || LOCALE_DEFAULT;

                // GDPR - We need to double check the languageSelect cookie to make sure this aligns with
                // the current locale. GDPR has unique eu/uk domains so no need for a locale within the URL
                const cookieLocale = this.localeService.getLocale();
                if (cookieLocale && cookieLocale !== locale) {
                    locale = cookieLocale;
                }

                moment.locale(locale);
                this.localeHelperService.setUriLocale(locale);
            });
    }

    setupRouteParams() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                switchMap(() => this.activatedRoute.firstChild.params),
                takeUntil(this.destroy$)
            )
            .subscribe((params) => this.routeState.updatePathParamState(params));
    }

    /* istanbul ignore next */
    getElementReference() {
        // Added to make testing reachable
        return this.elementRef.nativeElement;
    }

    private setupTheme() {
        this.themeSvc.themeChange()
            .pipe(takeUntil(this.destroy$))
            .subscribe(change => {
                // The first value passed in should be thrown away. It's "null"
                if (!change) {
                    return;
                }

                // TODO: Why are we doing this?
                const body = this.getElementReference().closest('body');
                body.classList.remove(this.theme);
                body.classList.add(change);
                this.theme = change;
            });

        // Initialize default theme.
        this.themeSvc.setCurrentTheme('snowball');
    }

    private setupLoadingService() {
        this.loadingSvc.getLoadingState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(loaded => {
                this.dataLoaded = loaded;
                // if (this.nativeBridge.isEmbedded() && loaded) {
                //     // Tell native mobile app that the SPA is fully loaded.
                //     this.nativeBridge.sendReadyMessage();
                // }
            });

        this.loadingSvc.getErrorState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(error => this.dataError = error);
    }

    private setupLanguageBundle() {
        this.finderSvc.getLanguageBundle()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.languageBundle = data;
                this.dineToFinderError.initialize(data.dineToFinderError);
            });
    }

    private setupSeo() {
        this.seoService.setServerUrl(this.apiBase);
        this.seoService.init();
    }

    private setupAnalytics() {
        this.finderAnalyticsService.initAnalytics(this.apiBase);
    }

    private setupCssOverride() {
        const body = this.getElementReference().closest('body');
        // Add the normalized locale and the site ID as CSS classes to the body
        // to allow targeting by brand and locale
        body.classList.add(this.siteId, this.localeService.getNormalizedLocale());

        // Listen for url change and then attach custom css
        this.urlParamsService.onChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                /* istanbul ignore else */
                if (!result) {
                    return;
                }

                /* istanbul ignore next */
                this.setCss(this.urlParamsService.id);
            });
    }

    /* istanbul ignore next */
    private setCss(urlFriendly) {
        if (!urlFriendly) {
            return;
        }

        loadCssResource(
            this.winRef.nativeWindow.document,
            `/advanced-finder/css/${urlFriendly}.css?${this.configService.getValue('cacheBuster')}`
        );
    }

    private setupLayout() {
        // If we are embedded do not proceed
        if (this.isNativeEmbedded) {
            return;
        }

        // Layout service allows a component to overide the header/footer from within the component
        // The current usage is live commerce. This will allow the `live` component to specify a
        // different header/footer.
        this.layoutService.useCustomLayout.subscribe((result: CustomLayoutProperties) => {
            if (result.enabled === false) {
                return;
            }

            // Disable syndicated header
            this.useSyndicated = false;

            // Check if custom header/footer are already set
            if (this.currentCustomHeader !== result.header) {
                // Set values for next time
                this.currentCustomHeader = result.header;

               // Lazy load module
               this.lazyLoader.lazyLoadModule(result.header, this.customHeader);
            }

            if (this.currentCustomFooter !== result.footer) {
                // Set values for next time
                this.currentCustomFooter = result.footer;

               // Lazy load module
               this.lazyLoader.lazyLoadModule(result.footer, this.customFooter);
            }
        });
    }
}
